import { useRichTextEditorModule, CardLinkEntry, createCardLinkKey, SetCardLinkEntry, FetchExternalCardLinkData } from '@/store/global/appComponents/richTextEditor';
import { LinkedCard, CardType } from '@/store/main/StorymapCardDetails/types';
import * as storymapStoreModule from '@/pages/main/storymap/store';
import { allCards, isActivity, isTask, isSubtask } from '@/pages/main/storymap/store/use';

import { createDebugger } from '@/_core/utils/debug';
const debug = createDebugger('app-components:use:useRichTextEditor');

/**
 * Map of pending fetch promises, keyed by composite key.
 * Used to deduplicate in-flight external card fetches.
 */
const pendingFetches: Record<string, Promise<CardLinkEntry>> = {};

/**
 * Builds a LinkedCard from a local card on the current storymap.
 */
function buildLocalLinkedCard(cardId: string): LinkedCard {
    const boardStore = storymapStoreModule.useStorymapStore();
    const card = allCards.value.find((c) => c.id === cardId);

    return {
        id: cardId,
        title: card?.title || 'Deleted Card',
        storymapTitle: boardStore.state.name,
        color: card?.color || 0,
        key: card?.key || '',
        linkedItemKey: card?.linkedItemKey || '',
        cardType: card
            ? isActivity(card)
                ? CardType.Activity
                : isTask(card)
                ? CardType.Task
                : isSubtask(card)
                ? CardType.Subtask
                : CardType.UnscheduledSubtask
            : CardType.Unknown,
        isCardKeyEnabled: boardStore.state.isCardKeyEnabled,
        integrationToolType: boardStore.state.integrationToolType,
        isLinkedToIntegrationTool: card?.isLinkedToIntegrationTool || false,
        isTrashed: !card,
        isExternal: false,
    };
}

/**
 * Checks if the storymap store is initialized and has a valid slug.
 *
 * This check is deliberately strict (isInitialized && slug) because card link
 * resolution depends on having a valid storymap context. In non-storymap hosts
 * (feedback portal, insights, etc.), this will return false and card links will
 * be treated as external and fetched via FetchExternalCardLinkData.
 */
function isStorymapStoreAvailable(): boolean {
    try {
        const boardStore = storymapStoreModule.useStorymapStore();
        return boardStore.state.isInitialized && !!boardStore.state.slug;
    } catch {
        return false;
    }
}

/**
 * Gets the current storymap slug if available.
 */
function getCurrentStorymapSlug(): string | null {
    if (!isStorymapStoreAvailable()) {
        return null;
    }
    return storymapStoreModule.useStorymapStore().state.slug;
}

/**
 * Gets an existing card link entry from the store cache, or creates a new idle entry.
 * For local cards on the current storymap, this will synchronously resolve the data.
 *
 * @param cardId The card ID
 * @param storymapSlug Optional storymap slug (null means current storymap)
 * @returns The cached entry (may have status 'idle' if not yet loaded)
 */
function getOrCreateCardLinkEntry(cardId: string, storymapSlug?: string | null): CardLinkEntry {
    const store = useRichTextEditorModule();
    const key = createCardLinkKey(cardId, storymapSlug);

    // Return existing entry if present
    if (store.state.cardLinksByKey[key]) {
        return store.state.cardLinksByKey[key];
    }

    const currentSlug = getCurrentStorymapSlug();
    const isOnCurrentStorymap = isStorymapStoreAvailable() && (!storymapSlug || storymapSlug === currentSlug);

    if (isOnCurrentStorymap) {
        // Synchronously resolve local cards
        const linkedCard = buildLocalLinkedCard(cardId);
        const entry: CardLinkEntry = {
            linkedCard,
            status: 'loaded',
        };
        // Store it synchronously
        store.dispatch(new SetCardLinkEntry(cardId, storymapSlug || null, entry));
        return entry;
    }

    // For external cards, create an idle entry
    const entry: CardLinkEntry = {
        linkedCard: null,
        status: 'idle',
    };
    store.dispatch(new SetCardLinkEntry(cardId, storymapSlug || null, entry));
    return entry;
}

/**
 * Ensures card link data is loaded, fetching from backend if necessary.
 * Returns immediately if already loaded or errored.
 * Deduplicates in-flight fetches.
 *
 * This function uses the RichTextEditorStore's own FetchExternalCardLinkData action
 * and does NOT depend on StorymapCardDetails store.
 *
 * @param cardId The card ID
 * @param storymapSlug Optional storymap slug (null means current storymap)
 * @returns Promise resolving to the CardLinkEntry
 */
async function ensureCardLinkLoaded(cardId: string, storymapSlug?: string | null): Promise<CardLinkEntry> {
    const store = useRichTextEditorModule();
    const key = createCardLinkKey(cardId, storymapSlug);

    // Get or create the entry (this handles synchronous local resolution)
    let entry = getOrCreateCardLinkEntry(cardId, storymapSlug);

    // If already loaded or errored, return immediately
    if (entry.status === 'loaded' || entry.status === 'error') {
        return entry;
    }

    // If already loading, return the pending promise
    if (pendingFetches[key]) {
        return pendingFetches[key];
    }

    // Mark as loading
    entry = { ...entry, status: 'loading' };
    store.dispatch(new SetCardLinkEntry(cardId, storymapSlug || null, entry));

    // Start the fetch using the RichTextEditorStore's own action
    debug(`Fetching external card data for cardId: ${cardId} on storymap: ${storymapSlug}`);

    pendingFetches[key] = (async (): Promise<CardLinkEntry> => {
        try {
            // Dispatch FetchExternalCardLinkData - the action handles the CQRS query
            // and updates cardLinksByKey directly with the result
            await store.dispatch(new FetchExternalCardLinkData(cardId, storymapSlug || ''));

            // Read the updated entry from the store (action reducer has already set it)
            const resultEntry = store.state.cardLinksByKey[key];
            debug(`Card data fetched for ${cardId}:`, resultEntry?.linkedCard);

            return resultEntry || { linkedCard: null, status: 'error', lastError: 'Entry not found after fetch' };
        } catch (err: any) {
            console.error(`Error fetching card ${cardId}:`, err);

            const errorEntry: CardLinkEntry = {
                linkedCard: null,
                status: 'error',
                lastError: err?.message || 'Unknown error',
            };
            store.dispatch(new SetCardLinkEntry(cardId, storymapSlug || null, errorEntry));
            return errorEntry;
        } finally {
            delete pendingFetches[key];
        }
    })();

    return pendingFetches[key];
}

/**
 * Gets an existing card link entry from the store cache.
 * Does not create entries or trigger fetches.
 *
 * @param cardId The card ID
 * @param storymapSlug Optional storymap slug
 * @returns The cached entry or undefined if not found
 */
function getCardLinkEntry(cardId: string, storymapSlug?: string | null): CardLinkEntry | undefined {
    const store = useRichTextEditorModule();
    const key = createCardLinkKey(cardId, storymapSlug);
    return store.state.cardLinksByKey[key];
}

/**
 * Helper functions for RichTextEditor store interactions.
 * This facade provides a convenient API over the store module.
 */
export const richTextEditor = {
    getCardLinkEntry,
    getOrCreateCardLinkEntry,
    ensureCardLinkLoaded,
    getCurrentStorymapSlug,
    isStorymapStoreAvailable,
};
